import React, { useEffect, useState } from 'react';
import { api } from '../api.js';
import { Plus, Trash2, Save, Sparkles } from 'lucide-react';
import Editor from '@monaco-editor/react';
import { useT } from '../i18n/useT.js';
import { useAlert } from '../components/AlertProvider.jsx';

const AI_PROMPT_MAX_CHARS = 4000;

function parseScope(scopeRaw) {
  if (!scopeRaw) return {};
  if (typeof scopeRaw === 'object') return scopeRaw;
  if (typeof scopeRaw === 'string') {
    try {
      return JSON.parse(scopeRaw || '{}');
    } catch {
      return {};
    }
  }
  return {};
}

function inferTriggerFromScript(script) {
  const scope = parseScope(script?.scope_json ?? script?.scopeJson ?? {});
  if (scope?.trigger === 'afterRender' || scope?.trigger === 'beforeRender') return scope.trigger;

  const code = String(script?.code_js || script?.codeJs || '');
  if (/\bafterRender\s*\(/m.test(code)) return 'afterRender';
  return 'beforeRender';
}

function boilerplateForTrigger(targetTrigger) {
  if (targetTrigger === 'afterRender') {
    return `async function afterRender(req, res) {
  
}
afterRender(req, res);
`;
  }

  return `async function beforeRender(req, res) {
  
}
beforeRender(req, res);
`;
}

const SCRIPT_EXAMPLES = [
  {
    id: 'before-normalize-customer',
    label: 'beforeRender · Normalize customer + currency',
    trigger: 'beforeRender',
    code: `async function beforeRender(req, res) {
  req.data.customer = req.data.customer || {};
  req.data.customer.name = String(req.data.customer.name || 'Cliente final').trim();
  req.data.currency = (req.data.currency || 'MXN').toUpperCase();
}
beforeRender(req, res);
`,
  },
  {
    id: 'before-calc-totals',
    label: 'beforeRender · Calculate totals',
    trigger: 'beforeRender',
    code: `async function beforeRender(req, res) {
  const items = Array.isArray(req.data.items) ? req.data.items : [];
  const subtotal = items.reduce((sum, item) => {
    const qty = Number(item.quantity || 0);
    const price = Number(item.price || 0);
    return sum + qty * price;
  }, 0);
  const taxRate = Number(req.data.taxRate || 0.16);
  req.data.subtotal = Number(subtotal.toFixed(2));
  req.data.tax = Number((subtotal * taxRate).toFixed(2));
  req.data.grandTotal = Number((subtotal + req.data.tax).toFixed(2));
}
beforeRender(req, res);
`,
  },
  {
    id: 'before-format-date',
    label: 'beforeRender · Format date label',
    trigger: 'beforeRender',
    code: `async function beforeRender(req, res) {
  const source = req.data.date ? new Date(req.data.date) : new Date();
  req.data.dateLabel = source.toLocaleDateString('es-MX', {
    year: 'numeric', month: 'long', day: 'numeric'
  });
}
beforeRender(req, res);
`,
  },
  {
    id: 'before-line-numbers',
    label: 'beforeRender · Add line numbers',
    trigger: 'beforeRender',
    code: `async function beforeRender(req, res) {
  const items = Array.isArray(req.data.items) ? req.data.items : [];
  req.data.items = items.map((item, idx) => ({
    ...item,
    lineNumber: idx + 1,
    isEven: idx % 2 === 0
  }));
}
beforeRender(req, res);
`,
  },
  {
    id: 'before-validate-required',
    label: 'beforeRender · Validate required fields',
    trigger: 'beforeRender',
    code: `async function beforeRender(req, res) {
  const missing = [];
  if (!req.data.invoiceNumber) missing.push('invoiceNumber');
  if (!req.data.customer?.name) missing.push('customer.name');
  if (!Array.isArray(req.data.items) || req.data.items.length === 0) missing.push('items');
  if (missing.length) throw new Error('Faltan campos requeridos: ' + missing.join(', '));
}
beforeRender(req, res);
`,
  },
  {
    id: 'after-audit-meta',
    label: 'afterRender · Audit metadata',
    trigger: 'afterRender',
    code: `async function afterRender(req, res) {
  res.meta.generatedAt = new Date().toISOString();
  res.meta.templateName = req.template?.name || null;
  res.meta.generatedByScript = true;
}
afterRender(req, res);
`,
  },
  {
    id: 'after-safe-email-request',
    label: 'afterRender · Request safe email send',
    trigger: 'afterRender',
    code: `async function afterRender(req, res) {
  res.meta.sendEmail = {
    to: req.data.notifyTo || 'cliente@empresa.com',
    subject: 'Tu reporte está listo',
    text: 'Adjuntamos el PDF generado automáticamente.',
    filename: 'reporte-' + (req.data.invoiceNumber || 'sin-folio') + '.pdf'
  };
}
afterRender(req, res);
`,
  },
  {
    id: 'after-webhook-request',
    label: 'afterRender · Request webhook event',
    trigger: 'afterRender',
    code: `async function afterRender(req, res) {
  res.meta.webhook = {
    event: 'report.generated',
    payload: {
      templateId: req.template?.id || null,
      invoiceNumber: req.data.invoiceNumber || null,
      total: req.data.grandTotal || null
    }
  };
}
afterRender(req, res);
`,
  },
  {
    id: 'after-validate-output',
    label: 'afterRender · Validate output size',
    trigger: 'afterRender',
    code: `async function afterRender(req, res) {
  if (!res.content || res.content.length < 300) {
    throw new Error('Salida inválida: el documento generado es demasiado pequeño');
  }
}
afterRender(req, res);
`,
  },
  {
    id: 'after-summary-log',
    label: 'afterRender · Summary in job logs',
    trigger: 'afterRender',
    code: `async function afterRender(req, res) {
  const items = Array.isArray(req.data.items) ? req.data.items.length : 0;
  res.meta.summary = {
    items,
    total: req.data.grandTotal || 0,
    hasDiscount: (req.data.subtotal || 0) > (req.data.grandTotal || 0)
  };
  console.log('afterRender summary', JSON.stringify(res.meta.summary));
}
afterRender(req, res);
`,
  },
];

export default function ScriptsPage() {
  const t = useT();
  const { showAlert, showConfirm } = useAlert();
  const [scripts, setScripts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [code, setCode] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [loading, setLoading] = useState(true);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiGenerationAllowed, setAiGenerationAllowed] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newScriptName, setNewScriptName] = useState('');
  const [creatingScript, setCreatingScript] = useState(false);
  const [selectedExampleId, setSelectedExampleId] = useState('');
  const [trigger, setTrigger] = useState('beforeRender');

  const load = async () => {
    setLoading(true);
    try {
      const [scriptsRes, templatesRes] = await Promise.all([
        api.getScripts(),
        api.getTemplates({ page: 1, limit: 200 }),
      ]);
      setScripts(scriptsRes.items || []);
      setTemplates(templatesRes.items || []);
    } catch {
      /* */
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    let mounted = true;

    api.getMyBilling()
      .then((billing) => {
        if (!mounted) return;
        setAiGenerationAllowed(billing?.features?.aiGeneration !== false);
      })
      .catch(() => {
        if (!mounted) return;
        setAiGenerationAllowed(true);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const handleCreate = async () => {
    const name = newScriptName.trim();
    if (!name) {
      showAlert(t('scripts.scriptNameRequired', 'Script name is required.'), {
        tone: 'warning',
        title: t('billing.settingsTitle', 'Validation'),
      });
      return;
    }

    setCreatingScript(true);
    try {
      const s = await api.createScript({
        name,
        templateId: null,
        scopeJson: { trigger: 'beforeRender' },
        codeJs: '// beforeRender\nasync function beforeRender(req, res) {\n  req.data.generatedAt = new Date().toISOString();\n}\nbeforeRender(req, res);\n',
      });
      await load();
      setSelected(s);
      setCode(s.code_js);
      setTemplateId(s.template_id || '');
      setTrigger('beforeRender');
      setCreateDialogOpen(false);
      setNewScriptName('');
    } catch (err) {
      showAlert(err.message || t('scripts.createFailed', 'Could not create script.'), {
        tone: 'error',
        title: t('billing.loadFailed', 'Failed'),
      });
    } finally {
      setCreatingScript(false);
    }
  };

  const handleSelect = async (id) => {
    const s = await api.getScript(id);
    setSelected(s);
    setCode(s.code_js);
    setTemplateId(s.template_id || '');
    let parsedScope = s.scope_json || s.scopeJson || {};
    if (typeof parsedScope === 'string') {
      try {
        parsedScope = JSON.parse(parsedScope || '{}');
      } catch {
        parsedScope = {};
      }
    }
    setTrigger(parsedScope?.trigger === 'afterRender' ? 'afterRender' : 'beforeRender');
  };

  const handleSave = async () => {
    if (!selected) return;
    await api.updateScript(selected.id, {
      codeJs: code,
      templateId: templateId || null,
      scopeJson: { trigger },
    });
    showAlert(t('scripts.saved', 'Saved'), { tone: 'success', title: t('billing.settingsSaved', 'Saved') });
  };

  const handleDelete = async (id) => {
    const accepted = await showConfirm(t('scripts.confirmDelete', 'Delete?'), {
      title: t('scripts.title', 'Scripts'),
      confirmLabel: t('billing.yes', 'Yes'),
      cancelLabel: t('billing.cancel', 'Cancel'),
      tone: 'warning',
    });
    if (!accepted) return;
    await api.deleteScript(id);
    if (selected?.id === id) { setSelected(null); setCode(''); }
    load();
  };

  const handleAIGenerate = async () => {
    if (!aiGenerationAllowed) {
      showAlert(t('scripts.aiPlanRequired', 'AI generation is not available in your current plan.'), {
        tone: 'warning',
        title: t('billing.settingsTitle', 'Validation'),
      });
      return;
    }

    const prompt = aiPrompt.trim().slice(0, AI_PROMPT_MAX_CHARS);
    if (!prompt) return;
    setAiGenerating(true);
    try {
      const result = await api.aiGenerateScript(prompt);
      if (selected) {
        setCode(result.codeJs || '');
      } else {
        const name = prompt.slice(0, 40).replace(/[^a-zA-Z0-9 _-]/g, '') || 'AI Script';
        const s = await api.createScript({
          name: `AI: ${name}`,
          templateId: null,
          codeJs: result.codeJs || '',
        });
        await load();
        setSelected(s);
        setCode(s.code_js || result.codeJs || '');
        setTemplateId(s.template_id || '');
      }
      setAiDialogOpen(false);
      setAiPrompt('');
      showAlert(t('scripts.aiApplied', 'AI-generated script applied.'), { tone: 'success', title: 'AI' });
    } catch (err) {
      showAlert(err.message || t('scripts.aiFailed', 'AI generation failed.'), { tone: 'error', title: 'AI' });
    } finally {
      setAiGenerating(false);
    }
  };

  const handleInsertExample = async () => {
    if (!selectedExampleId) return;
    const example = SCRIPT_EXAMPLES.find((item) => item.id === selectedExampleId);
    if (!example) return;

    if (code.trim()) {
      const accepted = await showConfirm(
        t('scripts.confirmReplaceWithExample', 'This will replace current script code. Continue?'),
        {
          title: t('scripts.title', 'Scripts'),
          confirmLabel: t('billing.yes', 'Yes'),
          cancelLabel: t('billing.cancel', 'Cancel'),
          tone: 'warning',
        },
      );
      if (!accepted) return;
    }

    setCode(example.code);
    setTrigger(example.trigger || 'beforeRender');
    showAlert(t('scripts.exampleInserted', 'Example inserted in editor.'), {
      tone: 'success',
      title: t('scripts.title', 'Scripts'),
    });
  };

  const handleChangeTrigger = (value) => {
    setTrigger(value);
    if (!code.trim()) {
      setCode(boilerplateForTrigger(value));
    }
  };

  const handleInsertTriggerBase = async () => {
    const snippet = boilerplateForTrigger(trigger);
    if (code.trim()) {
      const accepted = await showConfirm(
        t('scripts.confirmReplaceWithBase', 'This will replace current script code with trigger base. Continue?'),
        {
          title: t('scripts.title', 'Scripts'),
          confirmLabel: t('billing.yes', 'Yes'),
          cancelLabel: t('billing.cancel', 'Cancel'),
          tone: 'warning',
        },
      );
      if (!accepted) return;
    }
    setCode(snippet);
    showAlert(t('scripts.baseInserted', 'Base script inserted.'), {
      tone: 'success',
      title: t('scripts.title', 'Scripts'),
    });
  };

  return (
    <>
      <div className="toolbar">
        <h2>{t('scripts.title', 'Scripts')}</h2>
        <div className="toolbar-actions">
          {selected && <button className="btn-primary" onClick={handleSave}><Save size={14} /> {t('scripts.save', 'Save')}</button>}
          <button className="btn-secondary" onClick={() => setAiDialogOpen(true)} disabled={aiGenerating || !aiGenerationAllowed}><Sparkles size={14} /> {t('scripts.aiGenerate', 'Generate with AI')}</button>
          <button
            className="btn-secondary"
            onClick={() => {
              setNewScriptName('');
              setCreateDialogOpen(true);
            }}
          >
            <Plus size={14} /> {t('scripts.new', 'New')}
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', height: 'calc(100vh - 110px)' }}>
        <div style={{ width: 240, borderRight: '1px solid var(--border)', overflowY: 'auto' }}>
          {scripts.map((s) => (
            <div key={s.id}
              onClick={() => handleSelect(s.id)}
              style={{ padding: '10px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: selected?.id === s.id ? 'var(--bg-surface)' : 'transparent' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                <span className={`badge ${inferTriggerFromScript(s) === 'afterRender' ? 'badge-info' : 'badge-warning'}`}>
                  {inferTriggerFromScript(s) === 'afterRender'
                    ? t('scripts.triggerAfter', 'after')
                    : t('scripts.triggerBefore', 'before')}
                </span>
              </div>
              <button style={{ background: 'none', color: 'var(--text-muted)', padding: 2 }} onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }}><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
        <div style={{ flex: 1 }}>
          {selected ? (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
                <label style={{ color: 'var(--text-muted)', fontSize: 12 }}>{t('scripts.template', 'Template')}</label>
                <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} style={{ minWidth: 260 }}>
                  <option value="">{t('scripts.globalAllTemplates', 'Global (all templates)')}</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <label style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 8 }}>
                  {t('scripts.trigger', 'Trigger')}
                </label>
                <select value={trigger} onChange={(e) => handleChangeTrigger(e.target.value)} style={{ minWidth: 160 }}>
                  <option value="beforeRender">beforeRender</option>
                  <option value="afterRender">afterRender</option>
                </select>
                <button className="btn-secondary" onClick={handleInsertTriggerBase}>
                  {t('scripts.insertBase', 'Insert base')}
                </button>
                <label style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 8 }}>
                  {t('scripts.exampleLabel', 'Example')}
                </label>
                <select
                  value={selectedExampleId}
                  onChange={(e) => setSelectedExampleId(e.target.value)}
                  style={{ minWidth: 320 }}
                >
                  <option value="">{t('scripts.selectExample', 'Select script example')}</option>
                  {SCRIPT_EXAMPLES.map((example) => (
                    <option key={example.id} value={example.id}>{example.label}</option>
                  ))}
                </select>
                <button className="btn-secondary" onClick={handleInsertExample} disabled={!selectedExampleId}>
                  {t('scripts.insertExample', 'Insert example')}
                </button>
              </div>
              <div style={{ flex: 1 }}>
                <Editor height="100%" language="javascript" theme="vs-dark" value={code} onChange={(v) => setCode(v || '')}
                  options={{ minimap: { enabled: false }, fontSize: 14, wordWrap: 'on', automaticLayout: true }} />
              </div>
            </div>
          ) : (
            <div className="empty-state"><p>{t('scripts.empty', 'Select or create a script')}</p></div>
          )}
        </div>
      </div>

      {/* AI Generation Dialog */}
      {aiDialogOpen && (
        <div className="modal-overlay" onClick={() => !aiGenerating && setAiDialogOpen(false)}>
          <div className="modal app-alert" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="app-alert__head">
              <Sparkles size={20} style={{ color: 'var(--accent)' }} />
              <h3 className="app-alert__title">{t('scripts.aiPromptTitle', 'Generate Script with AI')}</h3>
            </div>
            <textarea
              rows={4}
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value.slice(0, AI_PROMPT_MAX_CHARS))}
              placeholder={t('scripts.aiPromptPlaceholder', 'Describe what the script should do...')}
              style={{ width: '100%', marginBottom: 14, fontFamily: 'inherit', resize: 'vertical' }}
              disabled={aiGenerating}
              maxLength={AI_PROMPT_MAX_CHARS}
              autoFocus
            />
            <div style={{ marginBottom: 14, color: 'var(--text-muted)', fontSize: 12, textAlign: 'right' }}>
              {t('scripts.aiPromptCounter', '{{count}} / {{max}} characters', {
                count: aiPrompt.length,
                max: AI_PROMPT_MAX_CHARS,
              })}
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setAiDialogOpen(false)} disabled={aiGenerating}>
                {t('billing.cancel', 'Cancel')}
              </button>
              <button className="btn-primary" onClick={handleAIGenerate} disabled={aiGenerating || !aiPrompt.trim()}>
                {aiGenerating ? (
                  <><span className="inline-spinner" /> {t('scripts.aiGenerating', 'Generating...')}</>
                ) : (
                  <><Sparkles size={14} style={{ marginRight: 4 }} /> {t('scripts.aiGenerate', 'Generate with AI')}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Script Dialog */}
      {createDialogOpen && (
        <div className="modal-overlay" onClick={() => !creatingScript && setCreateDialogOpen(false)}>
          <div className="modal app-alert" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
            <div className="app-alert__head">
              <Plus size={20} style={{ color: 'var(--accent)' }} />
              <h3 className="app-alert__title">{t('scripts.newScriptTitle', 'Create New Script')}</h3>
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>{t('scripts.scriptNameLabel', 'Script name')}</label>
              <input
                value={newScriptName}
                onChange={(e) => setNewScriptName(e.target.value)}
                placeholder={t('scripts.scriptNamePlaceholder', 'Enter script name')}
                maxLength={255}
                disabled={creatingScript}
                autoFocus
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setCreateDialogOpen(false)} disabled={creatingScript}>
                {t('billing.cancel', 'Cancel')}
              </button>
              <button className="btn-primary" onClick={handleCreate} disabled={creatingScript || !newScriptName.trim()}>
                {creatingScript ? (
                  <><span className="inline-spinner" /> {t('scripts.creating', 'Creating...')}</>
                ) : (
                  <><Plus size={14} style={{ marginRight: 4 }} /> {t('scripts.create', 'Create')}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
